import { useCallback, useState } from "react";
import type { TaskKey, WorkbenchTask } from "~/lib/constants/workbench";
import * as m from "~/paraglide/messages.js";

export type Decision = "open" | "approved" | "held";

/**
 * The workbench's whole state: which task is on screen, and whether the visitor
 * has acted on it. Choosing a task clears the decision, mirroring the design's
 * `resetAction()`.
 */
export function useWorkbench(initial: TaskKey = "support") {
  const [selected, setSelected] = useState<TaskKey>(initial);
  const [decision, setDecision] = useState<Decision>("open");

  const selectTask = useCallback((key: TaskKey) => {
    setSelected(key);
    setDecision("open");
  }, []);

  const reset = useCallback(() => setDecision("open"), []);

  return { decision, reset, selected, selectTask, setDecision };
}

/** The three lines the approval box and status bar show, per task and decision. */
export function getWorkbenchStatus(task: WorkbenchTask, decision: Decision) {
  const needsPerson = task.needsPerson ?? false;

  if (decision === "approved") {
    return {
      description: needsPerson ? m.wb_desc_stops() : m.wb_desc_recorded(),
      isComplete: true,
      label: needsPerson ? m.wb_label_waiting_owner() : m.wb_label_approved(),
      state: task.approvedState,
    };
  }

  if (decision === "held") {
    return {
      description: m.wb_desc_paused(),
      isComplete: false,
      label: m.wb_label_paused(),
      state: m.wb_state_paused(),
    };
  }

  return {
    description: task.description,
    isComplete: false,
    label: needsPerson
      ? m.wb_label_needs_detail()
      : m.wb_label_needs_approval(),
    state: needsPerson ? m.wb_state_order() : m.wb_state_draft(),
  };
}
